import { createFileRoute, Outlet } from '@tanstack/react-router';
import { UnifiedCustomerPortalLayout } from '../customer-portal/components/UnifiedCustomerPortalLayout';
import { createOrgBasedLoader } from '../lib/portal-loaders';
import { PortalProvider } from '../contexts/PortalContext';
import { CustomerPortalSignalRProvider } from '../contexts/CustomerPortalSignalRContext';
import { Loader2 } from 'lucide-react';

// Error fallback component for organization validation errors
function OrganizationError({ error }: { error: Error }) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Organization Not Found
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {error.message || 'The organization you\'re looking for doesn\'t exist or has disabled their portal.'}
        </p>
        <button
          onClick={() => window.location.href = '/'}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Go Home
        </button>
      </div>
    </div>
  );
}

// Loading component
function PortalLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
        <p className="text-gray-600 dark:text-gray-400">
          Validating organization...
        </p>
      </div>
    </div>
  );
}

// Layout component that wraps portal routes
function PortalLayout() {
  const loaderData = Route.useLoaderData();

  return (
    <PortalProvider value={loaderData}>
      <CustomerPortalSignalRProvider organizationId={loaderData.organizationInfo.organizationId}>
        <UnifiedCustomerPortalLayout organizationInfo={loaderData.organizationInfo}>
          <Outlet />
        </UnifiedCustomerPortalLayout>
      </CustomerPortalSignalRProvider>
    </PortalProvider>
  );
}

export const Route = createFileRoute('/org/$orgSlug/portal')({
  loader: createOrgBasedLoader(),
  component: PortalLayout,
  pendingComponent: PortalLoading,
  errorComponent: ({ error }) => <OrganizationError error={error} />,
});